import {
  IonContent,
  IonLoading,
  IonPage,
  useIonAlert,
} from "@ionic/react";
import React, { useCallback, useContext, useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import { useParams } from "react-router";
import { decodeParam } from "../util/ApiHelper";
import { WoDocument } from "../models/workorders/Documents";
import { useDocumentManager } from "../hooks/useDocumentManager";
import { getDocumentsLinkedToEquipment } from "../api/EquipmentDocuments";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import DocumentListDisplay from "../components/DocumentListDisplay";

const EquipmentDocuments: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const { equipmentId } = useParams<{ equipmentId: string }>();
  const displayEquipment = decodeParam(equipmentId);
  const [showLoading, setShowLoading] = useState(false);

  const [documents, setDocuments] = useState<WoDocument[]>([]);
  const [errorData, setErrorData] = useState("");
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { viewDocument } = useDocumentManager();
  const [presentAlert] = useIonAlert();

  useEffect(() => {
    setShowLoading(true);
    getDocumentsLinkedToEquipment({
      equipment: displayEquipment,
    })
      .then((response) => {
        if (response.isError) {
          setErrorData(response.data?.toString());
          setDocuments([]);
        } else if (response.data) {
          if (response.data && response.data.length > 0) {
            setDocuments(response.data);
          } else {
            setDocuments([]);
          }
        }
      })
      .finally(() => {
        setShowLoading(false);
      });
  }, [displayEquipment]);

  const showAlert = useCallback(
    (message: string) => {
      presentAlert({
        message: message,
        buttons: ["OK"],
      });
    },
    [presentAlert]
  );

  const onDocumentClick = useCallback(
    async (document: WoDocument) => {
      if (document && !!document.DownloadURL) {
        setShowLoading(true);

        await viewDocument({
          WoNumber: displayEquipment,
          Document: document,
        });
        setShowLoading(false);
      } else {
        showAlert(
          translatedMessages["DocProviderMissing"]?.MessageText ||
            "Cannot Open this document as document source information is missing"
        );
      }
    },
    [displayEquipment, showAlert, translatedMessages, viewDocument]
  );

  useEffect(() => {
    if (documents.length === 1) {
      onDocumentClick(documents[0]);
    }
  }, [documents.length]);

  return (
    <IonPage>
      <IonLoading isOpen={showLoading} />
      <PageHeader title={displayEquipment} showBack />
      <IonContent class="ion-padding">
        <h3>{translations["lbl_mob_inventoryitem_docs"] || "Documents"}</h3>
        <DocumentListDisplay
          documents={documents}
          onDocumentClick={onDocumentClick}
          translations={translations}
          translatedMessages={translatedMessages}
          showLoading={showLoading}
          errorData={errorData}
        />
      </IonContent>
    </IonPage>
  );
};

export default withAITracking(
  reactPlugin,
  EquipmentDocuments,
  "EquipmentDocuments"
);
